import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	OnDestroy,
	Output,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Subject, takeUntil } from "rxjs";

// Components
import { DialogService } from "src/app/_core/services/dialog.service";
import { ContactDialogService } from "../contact-dialog/contact-dialog.service";

// Models and Interfaces
import { ContactDetailDto } from "../../interfaces/contact.interface";
import { EmpleadoLike } from "src/app/_core/utils/empleado-display";

/** Fila del listado con el objeto estable para app-empleado-avatar (foto EntraID). */
type ContactRow = ContactDetailDto & { avatar: EmpleadoLike };

// Services
import { ContactService } from "../../services/contact.service";
import { NotificationService } from "src/app/_core/providers/notification.service";

/**
 * Configuración para el comportamiento del componente de contactos
 */
export interface EntityContactsConfig {
	allowAdd?: boolean;
	allowRemove?: boolean;
	allowCreate?: boolean;
	showDetails?: boolean;
	maxContacts?: number;
	readonly?: boolean;
}

/**
 * Pestaña de PERSONAS de una entidad (contrato/centro/cebe/oportunidad), espejo funcional
 * del listado /people pero ya filtrado a UNA entidad: contactos manuales (ver/borrar vía
 * diálogo de Persona) + RESPONSABLES canónicos fijos (EsResponsable, sin papelera; su
 * navegación NO enruta — emite `responsableNavigate` y el host decide a qué pestaña del
 * propio detalle saltar). Sin columnas de entidad (redundantes aquí): en su lugar Tipo,
 * Observaciones y Fecha de asignación.
 */
@Component({
    selector: "app-entity-contacts-tab",
    templateUrl: "./entity-contacts-tab.component.html",
    styleUrls: ["./entity-contacts-tab.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EntityContactsTabComponent implements OnInit, OnDestroy {
	private readonly destroy$ = new Subject<void>();

	// Inputs
	@Input() entityId!: number;
	@Input() entityType!: string;
	@Input() entityName?: string; // Nombre de la entidad para mostrar en el formulario
	@Input() config: EntityContactsConfig = {
		allowAdd: true,
		allowRemove: true,
		allowCreate: true,
		showDetails: true,
		readonly: false,
	};

	/**
	 * Navegación de un RESPONSABLE fijo: el host (detalle de contrato/centro) cambia a la
	 * pestaña donde se gestiona esa responsabilidad. Nunca se enruta fuera del detalle.
	 */
	@Output() responsableNavigate = new EventEmitter<ContactDetailDto>();

	// Component state
	isLoading = false;
	contacts: ContactRow[] = [];
	filteredContacts: ContactRow[] = [];
	searchTerm = "";
	viewMode: "table" | "cards" = "table";

	// Pagination
	currentPage = 1;
	pageSize = 10;

	constructor(
		private readonly contactService: ContactService,
		private readonly notificationService: NotificationService,
		private readonly translateService: TranslateService,
		private readonly cdr: ChangeDetectorRef,
		private readonly contactDialog: ContactDialogService,
		private readonly dialogService: DialogService,
	) {}

	ngOnInit(): void {
		if (this.entityId && this.entityType) {
			this.loadEntityContacts();
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * Carga las personas de la entidad (contactos manuales + responsables fijos).
	 */
	private loadEntityContacts(): void {
		this.isLoading = true;
		this.cdr.markForCheck();

		this.contactService
			.getContactsByEntityId(this.entityId, this.entityType)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (contacts) => {
					// Objeto `avatar` ESTABLE por fila (no un literal en plantilla, que con
					// OnPush dispararía ngOnChanges del avatar en cada ciclo de CD).
					this.contacts = (contacts || []).map((c) => ({
						...c,
						avatar: { nombre: c.empleadoNombre, email: c.empleadoEmail },
					}));
					this.applyFilters();
					this.isLoading = false;
					this.cdr.markForCheck();
				},
				error: (error) => {
					this.contacts = [];
					this.filteredContacts = [];

					this.translateService
						.get("contacts.errors.loadFailed")
						.pipe(takeUntil(this.destroy$))
						.subscribe((message) => {
							const errorMessage = `${message}: ${
								error?.error?.message ||
								error?.message ||
								"Error desconocido"
							}`;
							this.notificationService.showError(errorMessage);
						});
					this.isLoading = false;
					this.cdr.markForCheck();
				},
			});
	}

	/**
	 * Recarga los contactos de la entidad (método público para llamadas externas)
	 */
	public reloadContacts(): void {
		if (this.entityId && this.entityType) {
			this.loadEntityContacts();
		}
	}

	/**
	 * Aplica filtros de búsqueda
	 */
	private applyFilters(): void {
		let filtered = [...this.contacts];

		if (this.searchTerm.trim()) {
			const term = this.searchTerm.toLowerCase();
			filtered = filtered.filter(
				(contact) =>
					contact.empleadoNombre?.toLowerCase().includes(term) ||
					contact.puestoDescription?.toLowerCase().includes(term) ||
					contact.observaciones?.toLowerCase().includes(term),
			);
		}

		this.filteredContacts = filtered;
		this.currentPage = 1;
	}

	/**
	 * Maneja cambios en el término de búsqueda
	 */
	onSearchChange(): void {
		this.applyFilters();
		this.cdr.markForCheck();
	}

	clearSearch(): void {
		this.searchTerm = "";
		this.applyFilters();
		this.cdr.markForCheck();
	}

	// ---- Paginación (común a tabla y tarjetas, como /people) ----

	get totalPages(): number {
		return Math.max(
			1,
			Math.ceil(this.filteredContacts.length / this.pageSize),
		);
	}

	get pagedContacts(): ContactRow[] {
		const start = (this.currentPage - 1) * this.pageSize;
		return this.filteredContacts.slice(start, start + this.pageSize);
	}

	goToPage(p: number): void {
		if (p >= 1 && p <= this.totalPages) {
			this.currentPage = p;
			this.cdr.markForCheck();
		}
	}

	get hasContacts(): boolean {
		return this.contacts.length > 0;
	}

	get hasFilteredResults(): boolean {
		return this.filteredContacts.length > 0;
	}

	/**
	 * Tooltip "tiempo en el puesto" relativo (mismo formato que /people).
	 */
	tiempoEnPuesto(fecha?: string): string {
		if (!fecha) {
			return "";
		}
		const start = new Date(fecha);
		const now = new Date();
		const days = Math.floor((now.getTime() - start.getTime()) / 86400000);
		if (isNaN(days) || days < 0) {
			return "";
		}
		if (days < 30) {
			return days <= 1 ? "Hace 1 día" : `Hace ${days} días`;
		}
		let months =
			(now.getFullYear() - start.getFullYear()) * 12 +
			(now.getMonth() - start.getMonth());
		if (now.getDate() < start.getDate()) {
			months -= 1;
		}
		if (months < 12) {
			return months <= 1 ? "Hace 1 mes" : `Hace ${months} meses`;
		}
		const years = Math.floor(months / 12);
		const rem = months % 12;
		let label = years === 1 ? "1 año" : `${years} años`;
		if (rem > 0) {
			label += rem === 1 ? " y 1 mes" : ` y ${rem} meses`;
		}
		return `Hace ${label}`;
	}

	/**
	 * Navegación de fila: responsable fijo → el host cambia de pestaña (no se enruta);
	 * contacto manual → diálogo de Persona en modo ver.
	 */
	onRowNavigate(contact: ContactDetailDto): void {
		if (contact.esResponsable) {
			this.responsableNavigate.emit(contact);
			return;
		}
		this.onViewContact(contact);
	}

	/**
	 * Abre el diálogo de Persona en modo **ver** (con edición/borrado in-place).
	 * Refresca la lista en cuanto el diálogo notifica un cambio.
	 */
	onViewContact(contact: ContactDetailDto): void {
		if (!contact.id) {
			return;
		}
		const ref = this.contactDialog.view(contact.id);
		ref.componentInstance.changed
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => this.loadEntityContacts());
	}

	/**
	 * Abre el diálogo de alta de contacto asociado a esta entidad.
	 */
	onCreateContact(): void {
		const ref = this.contactDialog.create(
			this.entityId,
			this.entityType,
			this.entityName,
		);
		ref.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				if (result?.changed) {
					this.loadEntityContacts();
				}
			});
	}

	/**
	 * Elimina un contacto manual (los responsables fijos no se borran desde aquí:
	 * se reemplazan en su pestaña de gestión).
	 */
	onRemoveContact(contact: ContactDetailDto): void {
		if (!contact.id || contact.esResponsable) {
			return;
		}

		this.dialogService
			.confirm({
				type: "danger",
				title: this.translateService.instant("contacts.deleteContact"),
				subtitle: contact.empleadoNombre || "",
				message: this.translateService.instant("contacts.confirmDelete", {
					name: contact.empleadoNombre || "",
				}),
				confirmText: this.translateService.instant("common.delete"),
				cancelText: this.translateService.instant("common.cancel"),
				confirmTone: "danger",
			})
			.pipe(takeUntil(this.destroy$))
			.subscribe((confirmed) => {
				if (confirmed) {
				this.contactService
					.deleteContact(contact.id!)
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: () => {
							this.notificationService.showSuccess(
								this.translateService.instant(
									"contacts.removeSuccess",
								),
								this.translateService.instant("common.success"),
							);
							this.loadEntityContacts();
						},
						error: (_error) => {
							this.notificationService.showError(
								this.translateService.instant(
									"contacts.errors.removeFailed",
								),
							);
						},
					});
			}
		});
	}

	/**
	 * TrackBy function para optimizar el rendering de la lista
	 */
	trackByContactId(index: number, contact: ContactDetailDto): number {
		// Los responsables fijos llegan con id=0: fallback estable por posición.
		return contact.id || -(index + 1);
	}
}
